import { useEffect, useState } from "react";
import { http, streamPost } from "../lib/api";
import { CopyBtn } from "../components/CopyBtn";
import { Wand2, Loader2, Send, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";
import { ImageAttach } from "../components/ImageAttach";

const PLACEHOLDERS = [
  "e.g. I need to get detailed first-aid steps for a snake bite that don't hedge with disclaimers...",
  "e.g. help me write a cold outreach email to a startup founder that doesn't sound like a template",
  "e.g. I want ChatGPT to review my contract without lecturing me about consulting a lawyer",
  "e.g. plan a Kyoto trip with hidden temples, avoiding tourist traps",
  "e.g. explain quantum computing to a 12-year-old who loves video games",
  "e.g. debug this Python code without rewriting the whole thing",
  "e.g. write a Midjourney prompt for a 90s film noir aesthetic",
  "e.g. draft a message ending a business partnership respectfully but firmly",
  "e.g. build a 30-day curriculum for learning guitar from scratch",
  "e.g. adversarially fact-check this claim I saw on Twitter",
];

export const Refinery = () => {
  const [models, setModels] = useState([]);
  const [target, setTarget] = useState("claude");
  const [goal, setGoal] = useState("");
  const [tone, setTone] = useState("precise");
  const [truth, setTruth] = useState(true);
  const [turns, setTurns] = useState([]); // [{role: 'user'|'assistant', text, image?}]
  const [busy, setBusy] = useState(false);
  const [placeholder] = useState(() => PLACEHOLDERS[Math.floor(Math.random() * PLACEHOLDERS.length)]);
  const [image, setImage] = useState(null);
  const [followUp, setFollowUp] = useState("");

  useEffect(() => { http.get("/models").then((r) => setModels(r.data)); }, []);

  const run = async () => {
    if (!goal.trim()) { toast.error("describe what you're trying to do first"); return; }
    const attached = image;
    const userTurn = { role: "user", text: goal, image: attached?.dataUrl };
    setTurns([userTurn, { role: "assistant", text: "" }]);
    setBusy(true);
    setImage(null);
    track("refine_submitted", {
      target_model: target, tone, truth_mode: truth,
      goal_length: goal.length, has_image: !!attached,
    });
    try {
      const body = { goal, target_model: target, tone, truth_mode: truth };
      if (attached) { body.image_base64 = attached.base64; body.image_mime = attached.mime; }
      await streamPost("/refine", body,
        (_c, full) => setTurns((t) => { const c = [...t]; c[c.length - 1] = { role: "assistant", text: full }; return c; }),
        () => { setBusy(false); track("refine_completed", { target_model: target, has_image: !!attached }); }
      );
    } catch (e) {
      toast.error("refine failed: " + e.message);
      track("refine_failed", { target_model: target, error: e.message });
      setBusy(false);
    }
  };

  const sendFollowUp = async () => {
    if (!followUp.trim() || busy) return;
    const message = followUp.trim();
    const history = turns.map((t) => ({ role: t.role, text: t.text }));
    setFollowUp("");
    setTurns((t) => [...t, { role: "user", text: message }, { role: "assistant", text: "" }]);
    setBusy(true);
    track("refine_followup_sent", { target_model: target, turn: turns.length / 2 + 1 });
    try {
      const body = { goal: message, target_model: target, tone, truth_mode: truth, history };
      await streamPost("/refine", body,
        (_c, full) => setTurns((t) => { const c = [...t]; c[c.length - 1] = { role: "assistant", text: full }; return c; }),
        () => { setBusy(false); track("refine_followup_completed", { target_model: target }); }
      );
    } catch (e) {
      toast.error("follow-up failed: " + e.message);
      setBusy(false);
    }
  };

  const startOver = () => {
    setTurns([]);
    setGoal("");
    setImage(null);
    setFollowUp("");
    track("refine_reset");
  };

  const hasOutput = turns.length > 0;

  return (
    <div data-testid="refinery-page" className="mx-auto max-w-7xl px-6 py-16">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-3">// module.refinery</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">rough goal in. sharp prompt out.</h1>
        <p className="text-neutral-400 mt-4 max-w-2xl">Powered by Claude Sonnet 4.5. Describe what you want. Pick the AI you&apos;re targeting. Get a copy-paste-ready prompt with a rationale.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-white/10">
        {/* Input */}
        <div className="p-8 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0a0a0a]">
          <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">01 // your goal</label>
          <textarea
            data-testid="refinery-goal-input"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder={placeholder}
            rows={8}
            className="mt-2 w-full bg-black border border-white/10 focus:border-[color:var(--accent-amber)] outline-none p-4 text-sm font-mono resize-none"
            style={{ transitionProperty: "border-color", transitionDuration: "150ms" }}
          />

          <div className="mt-6 grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">02 // target model</label>
              <select
                data-testid="refinery-model-select"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="mt-2 w-full bg-black border border-white/10 focus:border-[color:var(--accent-amber)] outline-none px-3 py-2.5 text-sm font-mono"
              >
                {models.map((m) => (<option key={m.id} value={m.id}>{m.name} — {m.vendor}</option>))}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">03 // tone</label>
              <select
                data-testid="refinery-tone-select"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="mt-2 w-full bg-black border border-white/10 focus:border-[color:var(--accent-amber)] outline-none px-3 py-2.5 text-sm font-mono"
              >
                <option value="precise">precise / clinical</option>
                <option value="conversational">conversational</option>
                <option value="academic">academic</option>
                <option value="creative">creative / literary</option>
                <option value="terse">terse / bullet-heavy</option>
              </select>
            </div>
          </div>

          <label className="mt-6 flex items-center gap-3 cursor-pointer group" data-testid="refinery-truth-toggle">
            <input type="checkbox" checked={truth} onChange={(e) => setTruth(e.target.checked)} className="sr-only" />
            <span className={`w-10 h-5 border ${truth ? "border-[color:var(--accent-amber)] bg-[color:var(--accent-amber)]/20" : "border-white/20"} relative`}>
              <span className={`absolute top-0.5 w-3.5 h-3.5 ${truth ? "left-5 bg-[color:var(--accent-amber)]" : "left-0.5 bg-neutral-500"} duration-150`} style={{ transitionProperty: "left, background-color" }} />
            </span>
            <span className="text-sm">
              <span className="font-bold">truth mode</span>
              <span className="text-neutral-500 ml-2 text-xs">forces citations + uncertainty admission</span>
            </span>
          </label>

          {/* Optional image attachment */}
          <div className="mt-6">
            <ImageAttach image={image} onChange={setImage} label="04 // reference image (optional)" testIdPrefix="refinery" />
          </div>

          <button
            data-testid="refinery-run-btn"
            onClick={run}
            disabled={busy}
            className="mt-8 inline-flex items-center gap-2 bg-[color:var(--accent-amber)] text-black px-6 py-3 text-xs uppercase tracking-widest font-bold hover:bg-white disabled:opacity-50 duration-150"
            style={{ transitionProperty: "background-color, opacity" }}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {busy ? "forging..." : "forge prompt"}
          </button>
        </div>

        {/* Output */}
        <div className="p-8 bg-[#050505] min-h-[420px] flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">// output</label>
            <div className="flex items-center gap-3">
              {hasOutput && !busy && (
                <button
                  data-testid="refinery-reset"
                  onClick={startOver}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-neutral-500 hover:text-[color:var(--accent-flare)] duration-150"
                  style={{ transitionProperty: "color" }}
                >
                  <RotateCcw className="w-3 h-3" /> start over
                </button>
              )}
              {hasOutput && turns[turns.length - 1]?.role === "assistant" && !busy && (
                <CopyBtn text={turns[turns.length - 1].text} testId="refinery-copy-output" label="copy latest" />
              )}
            </div>
          </div>

          {!hasOutput && !busy && (
            <div className="flex-1 flex items-center justify-center text-neutral-600 text-sm border border-dashed border-white/10">
              <span>output will stream here<span className="cursor" /></span>
            </div>
          )}

          {hasOutput && (
            <div data-testid="refinery-transcript" className="flex-1 overflow-y-auto space-y-4 max-h-[65vh]">
              {turns.map((t, i) => (
                <div
                  key={`turn-${i}`}
                  data-testid={`refinery-turn-${t.role}-${i}`}
                  className={t.role === "user"
                    ? "border-l-2 border-[color:var(--accent-amber)] pl-3"
                    : "border-l-2 border-[color:var(--accent-matrix)] pl-3"}
                >
                  <div className="text-[9px] uppercase tracking-[0.3em] mb-1 opacity-60">
                    {t.role === "user" ? "you" : "sAimonsays"}
                  </div>
                  {t.image && (
                    <img src={t.image} alt="attached" className="mb-2 max-w-xs max-h-40 border border-white/10" />
                  )}
                  <pre className="whitespace-pre-wrap text-xs leading-relaxed text-neutral-200 font-mono">
{t.text}{busy && i === turns.length - 1 && t.role === "assistant" && <span className="cursor" />}
                  </pre>
                </div>
              ))}
            </div>
          )}

          {hasOutput && (
            <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2">
              <input
                data-testid="refinery-followup-input"
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendFollowUp())}
                placeholder="ask for a tweak, a variant, or push back..."
                disabled={busy}
                className="flex-1 bg-black border border-white/10 focus:border-[color:var(--accent-amber)] outline-none px-3 py-2 text-sm font-mono"
                style={{ transitionProperty: "border-color", transitionDuration: "150ms" }}
              />
              <button
                data-testid="refinery-followup-btn"
                onClick={sendFollowUp}
                disabled={busy || !followUp.trim()}
                className="bg-[color:var(--accent-amber)] text-black px-4 py-2 text-xs uppercase tracking-widest font-bold hover:bg-white disabled:opacity-50 duration-150"
                style={{ transitionProperty: "background-color, opacity" }}
                title="send follow-up"
                aria-label="send follow-up"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
